import { useViewport } from 'contexts/viewport';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FormLabel, Icon, Paper, Stack, styled, Switch, Text, ViewMore } from 'shell/components';
import { isFeedbackSettingsDefault } from 'utils/helper';

const FeedbackSection = memo(
    ({ isEditable, form, setForm, errors, setFeedbackSettingsDialog, openVideoFeedbackSettings }) => {
        const { device } = useViewport();
        const { t } = useTranslation('assignment');

        const getText = () => {
            if (form.feedbackSettings.structure === 'rubric') {
                return t(
                    'feedback-section.rubric-based-feedback-with-feedbacktype',
                    'Rubric-based feedback ({{feedbackType}})',
                    {
                        feedbackType: isFeedbackSettingsDefault(form.feedbackSettings)
                            ? t('system-default', 'system default?')
                            : t('edited', 'Edited'),
                    }
                );
            }

            if (form.feedbackSettings.structure === 'system') {
                return t('feedback-section.system-based-feedback', 'General & detailed feedback');
            }

            if (form.feedbackSettings.structure === 'templated') {
                return errors.feedbackSettings
                    ? t('feedback-section.templated-feedback-with-error', 'Smart Feedback Templates {{error}}', {
                          error: errors.feedbackSettings,
                      })
                    : t('feedback-section.templated-feedback', 'Smart Feedback Templates');
            }

            return '';
        };

        let isAdvancedSettingsDisabled;
        if (process.env.REACT_APP_AUTH === 'PASSWORD') {
            isAdvancedSettingsDisabled = !isEditable || !form.aiFeedbackEnabled;
        } else {
            isAdvancedSettingsDisabled = !isEditable || (!form.aiFeedbackEnabled && !form.aiStudentFeedbackEnabled);
        }

        return (
            <Stack sx={{ width: '100%' }} spacing={1}>
                <Text variant={'p1'}>{t('feedback-section.feedback-label', 'Feedback')}</Text>
                <CustomPaper variant={'outlined'}>
                    <Stack spacing={4}>
                        <CustomPaper variant={'outlined'}>
                            <Stack spacing={3}>
                                <Stack
                                    alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                                    direction={'row'}
                                    justifyContent={'space-between'}
                                >
                                    <Stack
                                        alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                                        direction={device === 'mobile' ? 'column-reverse' : 'row'}
                                        spacing={device === 'mobile' ? 2 : 4}
                                    >
                                        <Text variant={'p1'}>{t('feedback-section.title', 'Feedback Settings')}</Text>
                                        <Button
                                            startIcon={<Icon kind={'video'} />}
                                            variant={'borderless'}
                                            onClick={() => openVideoFeedbackSettings()}
                                        >
                                            {t('feedback-section.watch-tutorial', 'Watch a Tutorial')}
                                        </Button>
                                    </Stack>
                                    <Button
                                        disabled={isAdvancedSettingsDisabled}
                                        variant={'borderless'}
                                        onClick={() => setFeedbackSettingsDialog({ open: true })}
                                    >
                                        {t('advanced-settings', 'Advanced Settings')}
                                    </Button>
                                </Stack>
                                {form.feedbackSettings.structure === 'custom' ? (
                                    <ViewMore color={'light'} rich text={form.feedbackSettings.customInstructions} />
                                ) : (
                                    <Text color={errors.feedbackSettings ? 'error' : 'light'} rich>
                                        {getText()}
                                    </Text>
                                )}
                            </Stack>
                        </CustomPaper>
                        <Stack spacing={2}>
                            <Stack
                                alignItems={'center'}
                                direction={'row'}
                                justifyContent={device === 'mobile' ? 'space-between' : 'initial'}
                                spacing={4}
                            >
                                <FormLabel id={'feedback-switch'}>
                                    <Text variant={'p1'}>
                                        {t(
                                            'feedback-section.final-submission-feedback-suggestions',
                                            'Final Submission Feedback Suggestions'
                                        )}
                                    </Text>
                                </FormLabel>
                                <Switch
                                    aria-labelledby={'feedback-switch'}
                                    checked={form.aiFeedbackEnabled}
                                    disabled={!isEditable}
                                    onChange={(_, value) =>
                                        setForm((current) => ({ ...current, aiFeedbackEnabled: value }))
                                    }
                                />
                            </Stack>
                            <Text>
                                {t(
                                    'feedback-section.feedback-description',
                                    "TimelyGrader leverages assignment details to provide instructors with personalized feedback suggestions for student's final submissions."
                                )}
                            </Text>
                        </Stack>
                        {process.env.REACT_APP_AUTH !== 'PASSWORD' && (
                            <Stack spacing={2}>
                                <Stack
                                    alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                                    direction={device === 'mobile' ? 'column' : 'row'}
                                    spacing={device === 'mobile' ? 2 : 4}
                                >
                                    <Stack
                                        alignItems={'center'}
                                        direction={'row'}
                                        justifyContent={device === 'mobile' ? 'space-between' : 'initial'}
                                        spacing={4}
                                    >
                                        <FormLabel id={'student-feedback-switch'}>
                                            <Text variant={'p1'}>
                                                {t(
                                                    'feedback-section.instant-feedback',
                                                    'Instant Feedback for Students'
                                                )}
                                            </Text>
                                        </FormLabel>
                                        <Switch
                                            aria-labelledby={'student-feedback-switch'}
                                            checked={form.aiStudentFeedbackEnabled}
                                            disabled={!isEditable || form.feedbackSettings.structure === 'templated'}
                                            onChange={(_, value) =>
                                                setForm((current) => ({ ...current, aiStudentFeedbackEnabled: value }))
                                            }
                                        />
                                    </Stack>
                                    {form.feedbackSettings.structure === 'templated' && (
                                        <Text color={'light'}>
                                            {t(
                                                'feedback-section.smart-feedback-unavailable',
                                                'Not available for Smart Feedback Templates'
                                            )}
                                        </Text>
                                    )}
                                </Stack>
                                <Text>
                                    {t(
                                        'feedback-section.instant-feedback-description',
                                        'Students can generate up to 3 rounds of AI feedback for assignments drafts before they submit. When you enable instant feedback, students will be able to see the assignment description and the assignment rubric.'
                                    )}
                                </Text>
                            </Stack>
                        )}
                    </Stack>
                </CustomPaper>
            </Stack>
        );
    }
);

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        padding: theme.spacing(4),
    };
});

export default FeedbackSection;
